// Ported from the native app's EconCalendar.swift.

export interface EconEvent {
  name: string;
  date: Date;
}

// FOMC rate decisions are announced 2:00pm ET on the second day of each
// meeting. Published by the Fed roughly a year in advance
// (federalreserve.gov/monetarypolicy/fomccalendars.htm) — needs a manual
// update here once the following year's schedule is announced.
const fomcDatesByYear: Record<number, Array<[number, number]>> = {
  2026: [
    [1, 28],
    [3, 18],
    [4, 29],
    [6, 17],
    [7, 29],
    [9, 16],
    [10, 28],
    [12, 9],
  ],
};

// CPI releases at 8:30am ET, published by the BLS a year in advance
// (bls.gov/schedule/news_release/cpi.htm) — same annual-update caveat.
const cpiDatesByYear: Record<number, Array<[number, number]>> = {
  2026: [
    [1, 13],
    [2, 13],
    [3, 11],
    [4, 10],
    [5, 12],
    [6, 10],
    [7, 14],
    [8, 12],
    [9, 11],
    [10, 14],
    [11, 10],
    [12, 10],
  ],
};

const etOffsetProbe = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/**
 * A Date for the given ET wall-clock time. JS has no zone-aware
 * constructor, so start from the same wall-clock reading as UTC and correct
 * by the actual ET offset at that instant (probing twice handles the DST
 * edge where the first guess lands on the wrong side of a transition).
 */
function etDate(year: number, month: number, day: number, hour: number, minute: number): Date {
  let guess = Date.UTC(year, month - 1, day, hour, minute);
  for (let i = 0; i < 2; i++) {
    const parts = etOffsetProbe.formatToParts(new Date(guess));
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
    const seenMinutes = (get("hour") % 24) * 60 + get("minute");
    const wantMinutes = hour * 60 + minute;
    const diff = wantMinutes - seenMinutes;
    if (diff === 0) break;
    guess += diff * 60_000;
  }
  return new Date(guess);
}

function fomcEvents(year: number): EconEvent[] {
  return (fomcDatesByYear[year] ?? []).map(([month, day]) => ({
    name: "FOMC rate decision",
    date: etDate(year, month, day, 14, 0),
  }));
}

function cpiEvents(year: number): EconEvent[] {
  return (cpiDatesByYear[year] ?? []).map(([month, day]) => ({
    name: "CPI release",
    date: etDate(year, month, day, 8, 30),
  }));
}

/**
 * NFP/Employment Situation is reliably released the first Friday of each
 * month at 8:30am ET — computed directly, no hardcoded dates needed.
 */
function nfpEvents(year: number): EconEvent[] {
  const events: EconEvent[] = [];
  for (let month = 1; month <= 12; month++) {
    // Day-of-week of an ET calendar date is zone-independent when probed at
    // noon UTC (no date rollover ambiguity).
    const firstOfMonth = new Date(Date.UTC(year, month - 1, 1, 12));
    const weekday = firstOfMonth.getUTCDay(); // 0 = Sunday
    const offsetToFriday = (5 - weekday + 7) % 7;
    events.push({ name: "Jobs report (NFP)", date: etDate(year, month, 1 + offsetToFriday, 8, 30) });
  }
  return events;
}

/**
 * Options expiry (OPEX) — the third Friday of each month, 4:00pm ET. The quarterly
 * ones (Mar / Jun / Sep / Dec) are "triple witching". Pure date math, so it's
 * deterministic and backtests honestly (D-091). Relevant to index / equity / their
 * futures — vol and pinning cluster here.
 */
function opexEvents(year: number): EconEvent[] {
  const events: EconEvent[] = [];
  for (let month = 1; month <= 12; month++) {
    const firstOfMonth = new Date(Date.UTC(year, month - 1, 1, 12));
    const weekday = firstOfMonth.getUTCDay();
    const firstFriday = 1 + ((5 - weekday + 7) % 7);
    const thirdFriday = firstFriday + 14;
    const witching = month === 3 || month === 6 || month === 9 || month === 12;
    events.push({ name: witching ? "Triple witching (OPEX)" : "Options expiry (OPEX)", date: etDate(year, month, thirdFriday, 16, 0) });
  }
  return events;
}

// --- Strategy-builder econ-calendar (D-065) --------------------------------
// The builder's `newsWindow` operand reads a list of {at (epoch ms), impact}.
// The SAME list is fed to the in-app backtester's evaluate() AND exported to the
// box as econ_calendar.json, so newsWindow resolves identically live and in
// backtest. FOMC / CPI / NFP are all high-impact macro prints.
export type EconKind = "fomc" | "cpi" | "nfp" | "opex";
export interface EconWindowEvent { at: number; impact: "high" | "medium" | "low"; name: string; country: string; kind: EconKind; }

function windowEventsForYears(fromYear: number, toYear: number): EconWindowEvent[] {
  const out: EconWindowEvent[] = [];
  const push = (evs: EconEvent[], kind: EconKind): void => {
    for (const e of evs) out.push({ at: e.date.getTime(), impact: "high", name: e.name, country: "US", kind });
  };
  for (let y = fromYear; y <= toYear; y++) {
    push(fomcEvents(y), "fomc");
    push(cpiEvents(y), "cpi");
    push(nfpEvents(y), "nfp");
    push(opexEvents(y), "opex");
  }
  return out.sort((a, b) => a.at - b.at);
}

/** Curated high-impact events overlapping [fromMs, toMs] — pass to `evaluate()` as
 * its 4th arg so an in-app backtest of a `newsWindow` strategy matches the box. */
export function econWindowEvents(fromMs: number, toMs: number): EconWindowEvent[] {
  const fy = new Date(fromMs).getUTCFullYear() - 1;
  const ty = new Date(toMs).getUTCFullYear() + 1;
  return windowEventsForYears(fy, ty).filter((e) => e.at >= fromMs - 86_400_000 && e.at <= toMs + 86_400_000);
}

/** The econ-calendar file the agent writes to the box (`~/.tickahead/econ_calendar.json`,
 * D-065). Curated, so refreshed on the same cadence as the FOMC/CPI tables above. */
export function econCalendarFile(now: number = Date.now()): { refreshedAt: number; events: EconWindowEvent[] } {
  const y = new Date(now).getUTCFullYear();
  return { refreshedAt: now, events: windowEventsForYears(y - 1, y + 2) };
}

export function upcomingEconEvents(limit = 5, now: Date = new Date()): EconEvent[] {
  const year = now.getFullYear();
  const all = [
    ...fomcEvents(year),
    ...cpiEvents(year),
    ...nfpEvents(year),
    ...opexEvents(year),
    ...fomcEvents(year + 1),
    ...cpiEvents(year + 1),
    ...nfpEvents(year + 1),
    ...opexEvents(year + 1),
  ];
  all.sort((a, b) => a.date.getTime() - b.date.getTime());
  return all.filter((e) => e.date.getTime() > now.getTime()).slice(0, limit);
}
